import { getFormatter } from 'next-intl/server';

import { ProductReviewFragment } from '@/fo/api/magento';
import { Review, ReviewService } from '@/fo/ui/review/catalog/base';

import { truncateText } from './truncateText';

const MAX_COMMENT_LENGTH = 300;

export const getRating = (rating: number) => {
  return rating / 20;
};

export const sourceMap: Record<string, ReviewService> = {
  'www.wildberries.ru': ReviewService.Wildberries,
  'www.ozon.ru': ReviewService.Ozon,
  'irecommend.ru': ReviewService.IRecommend,
  'otzovik.com': ReviewService.Otzovik,
  'market.yandex.ru': ReviewService.YandexMarket
};

export const getComment = (review: ProductReviewFragment) => {
  if (
    review.source &&
    [ReviewService.Otzovik, ReviewService.IRecommend].includes(
      sourceMap[review.source]
    )
  ) {
    if (review.text.length > MAX_COMMENT_LENGTH) {
      return truncateText(review.text, MAX_COMMENT_LENGTH);
    }
  }

  return review.text;
};

export const getShowMoreLink = (review: ProductReviewFragment) => {
  const isShowMore =
    review.source &&
    [ReviewService.Otzovik, ReviewService.IRecommend].includes(
      sourceMap[review.source]
    ) &&
    review.text.endsWith('...');

  return isShowMore ? review.source_url : null;
};

export const mapReviews = async (
  reviews: ProductReviewFragment[]
): Promise<Review[]> => {
  const format = await getFormatter();

  return reviews.map((review) => ({
    id: review.id,
    rating: getRating(review.average_rating),
    date: format.dateTime(new Date(review.created_at), {
      year: '2-digit',
      month: 'numeric',
      day: 'numeric'
    }),
    author: {
      name: review.nickname,
      age: review.age,
      skinType: review.skin_type
    },
    pros: review.pros ?? null,
    cons: review.cons ?? null,
    comment: getComment(review),
    showMoreLink: getShowMoreLink(review),
    ...(review.answer && {
      response: {
        text: review.answer,
        ...(review.answer_product &&
          review.answer_product?.name && {
            recommendedProduct: {
              id: review.answer_product?.sku ?? '',
              title: review.answer_product?.name ?? '',
              imageUrl: review.answer_product.thumbnail?.url ?? '',
              url: review.answer_product.url_key
                ? `/product/${review.answer_product.url_key}`
                : undefined
            }
          })
      }
    }),
    ...(review.is_external &&
      review.source && {
        source: {
          type: sourceMap[review.source],
          url: review.source_url ?? ''
        }
      }),
    images:
      review.images
        ?.filter((image) => image !== null)
        .map((image) => ({
          id: image,
          url: image,
          alt: '',
          reviewId: review.id,
          rating: getRating(review.average_rating),
          author: review.nickname
        })) ?? []
  }));
};
